import React, { forwardRef } from 'react';

import Loading from '@/assets/loading.svg?react';
import RightArrow from '@/assets/right-arrow.svg?react';
import cn from '@/utils/cn';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import constate from 'constate';

const button = cva(
  cn(
    'flex items-center justify-center gap-2 lg:gap-2.5',
    'transition-colors duration-200 ease-in-out',
    'visited:shadow-none hover:shadow-none focus:outline-none focus:ring-4 focus:ring-offset-0 active:shadow-none',
    'disabled:cursor-not-allowed disabled:shadow-none lg:w-fit [&>*]:flex [&>*]:items-center [&>*]:justify-center [&>*]:gap-2.5'
  ),
  {
    variants: {
      colors: {
        blue: [
          'bg-blue-600 hover:bg-blue-700 active:bg-blue-700 visited:bg-blue-700 disabled:text-blue-400 [&>svg>path]:disabled:fill-blue-400 disabled:bg-blue-300 focus:ring-blue-500/30 shadow-[0px_3px_12px_0px_rgba(0,81,212,0.20)]',
        ],
        red: [
          'bg-red-400 hover:bg-red-500 active:bg-red-500 visited:bg-red-500 disabled:text-dark-gray-300 [&>svg>path]:disabled:fill-dark-gray-300 disabled:bg-gray-500 focus:ring-red-400/30 shadow-[0px_3px_12px_0px_rgba(204,0,51,0.20)]',
        ],
        green: [
          'bg-green-400 hover:bg-green-500 active:bg-green-500 visited:bg-green-600 disabled:text-dark-gray-300 [&>svg>path]:disabled:fill-dark-gray-300 disabled:bg-gray-500 focus:ring-green-400/30 shadow-[0px_3px_12px_0px_rgba(56,177,55,0.20)]',
        ],
        oxfordBlue: [
          'bg-oxford-blue-100 hover:bg-oxford-blue-200 active:bg-oxford-blue-200 visited:bg-oxford-blue-200 disabled:text-dark-gray-300 [&>svg>path]:disabled:fill-dark-gray-300 disabled:bg-gray-500 focus:ring-oxford-blue-100/30',
        ],
        gold: [
          'bg-gold-800 text-gold-400 font-inter hover:brightness-110 disabled:text-dark-gray-300 [&>svg>path]:disabled:fill-dark-gray-300 disabled:bg-gray-200 disabled:brightness-100 focus:ring-yellow-400/30',
        ],
        yellow: [
          'bg-gold-400 text-dark-gray-700 font-inter hover:brightness-95 disabled:text-dark-gray-300 [&>svg>path]:disabled:fill-dark-gray-300 disabled:bg-yellow-100 disabled:brightness-100 focus:ring-yellow-400/30',
        ],
      },
      size: {
        xs: ['px-1.5 py-1 caption rounded gap-x-0.5'],
        small: ['px-4 py-2.5 button-link rounded-[10px]'],
        large: ['px-6 py-[18px] button-link rounded-[10px]'],
        deepDry: [
          'px-3 py-1.5 button-link rounded-[12px_4px_12px_4px] lg:px-3 lg:py-1.5',
        ],
        deepDryLarge: [
          'px-6 py-[18px] button-link rounded-[12px_4px_12px_4px] lg:px-6 lg:py-[18px]',
        ],
      },
      intent: {
        primary: ['text-gray-100'],
        secondary: [
          'bg-transparent text-blue-600 [&>div>svg>g>circle]:stroke-blue-600 hover:bg-blue-600 hover:text-gray-100 hover:[&>div>svg>g>circle]:stroke-gray-100 active:bg-blue-600 active:text-gray-100 visited:bg-blue-600 visited:text-gray-100 focus:ring-0 focus:text-blue-800 [&>div>svg>g>circle]:focus:stroke-blue-800 shadow-none disabled:text-blue-400 [&>div>svg>g>circle]:disabled:stroke-blue-400 disabled:bg-transparent',
        ],
        square: ['p-3 aspect-square [&>svg]:w-[18px] [&>svg]:h-[18px]'],
        deepDry: ['text-dark-gray-700'],
      },
    },
    defaultVariants: {
      intent: 'primary',
      colors: 'blue',
      size: 'large',
    },
  }
);

const [ButtonProvider, useButtonContext] = constate(
  ({ loading }: { loading?: boolean }) => {
    return {
      loading,
    };
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild, className, intent, size, colors, loading, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <ButtonProvider loading={loading}>
        <Comp
          className={cn(
            button({
              size,
              intent,
              colors,
              className,
            })
          )}
          ref={ref}
          disabled={loading}
          {...props}
        >
          {props.children}
        </Comp>
      </ButtonProvider>
    );
  }
);

export const ButtonLoading = ({
  className = 'text-blue-400',
}: {
  className?: string;
}) => {
  const { loading } = useButtonContext();
  if (!loading) return null;
  return <Loading className={cn('size-[18px]', className)} />;
};

export const ButtonRightArrow = ({ className }: { className?: string }) => {
  const { loading } = useButtonContext();
  if (loading) return null;
  return <RightArrow className={cn('size-[18px] shrink-0', className)} />;
};

Button.displayName = 'Button';

export default Button;
